import { useMemo, useState, type ReactNode } from 'react';
import { ExternalLink, Plus } from 'lucide-react';
import { Modal } from '../../Modal';
import { Badge, Button, Card, Input, OverflowMenu, Switch, Typography, type OverflowMenuAction } from '../ui';
import { editPersonCard } from '../../../lib/contact-book';
import type { CardEdit, StoredImportantDate, StoredSocialProfile } from '../../../lib/person-card-edit';
import { HEBREW_MONTHS, hebrewMonthName, type HebrewMonth } from '../../../lib/hebrew-calendar';
import {
  DATE_KIND_WIRE,
  NETWORK_NAMES,
  cardSummaryKeys,
  civilToday,
  currentHebrewYear,
  dateDraftValue,
  dateKindLabelKey,
  hebrewMonthsForYear,
  switchDateDraftCalendar,
  type DateDraft,
  daysUntilKey,
  formatCardDate,
  gregorianMonthNames,
  type CardDateKind,
  type CardDateRow,
  type CardNetwork,
  type CardOrganization,
  type CardRelationshipRow,
  type CardProfileRow,
  type PersonCardModel,
} from '../../../lib/person-card';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useNarrow } from '../../../lib/use-narrow';
import { useProductName } from '../../../lib/use-brand';
import { useTranslation } from '../../../lib/i18n';
import styles from './contacts.module.css';

/**
 * The person card's structured sections (plan P1.16) — Relationship, Important dates, Social profiles and
 * Work — under the identity on the contact detail screen. Everything shown comes from `personCardModel`
 * (`lib/person-card.ts`), which iOS and Android build identically from the same entry; this file only draws
 * it and collects edits. Every edit goes through `editPersonCard` → `/api/contact-person` as the user's own
 * value; "Forget this" asks first, with the copy that says the agent will not use or look it up again.
 *
 * A value the agent INFERRED wears "Suggested by Symphony"; editing it makes it the user's. The derived
 * signals (family / work / minor / closeness) are never drawn — they are behaviour, not content (§1.1).
 */

const PAGE = 'contact_detail';
const DATE_KINDS: CardDateKind[] = ['birthday', 'anniversary', 'memorial', 'nameDay', 'custom'];
const KIND_WIRE = DATE_KIND_WIRE;
const NETWORK_ORDER: CardNetwork[] = ['instagram', 'linkedin', 'x', 'facebook', 'tiktok', 'youtube', 'github', 'website', 'other'];

type Dialog =
  | { kind: 'relationship'; editing: CardRelationshipRow | null }
  | { kind: 'date'; editing: CardDateRow | null }
  | { kind: 'social' }
  | { kind: 'work'; editing: CardOrganization | null }
  | { kind: 'forget'; edit: CardEdit };

/** The wire value a relationship row was read as — the contract's `target` (relation + anchor). */
function relationshipWire(row: CardRelationshipRow): { relation: string; relatedToPersonId?: string } {
  return { relation: row.text, ...(row.relatedToPersonId ? { relatedToPersonId: row.relatedToPersonId } : {}) };
}

/** The wire value a date row was read as — the row's key on the server (`lib/person-card-edit.ts`). */
function dateWire(row: CardDateRow): StoredImportantDate {
  return {
    kind: KIND_WIRE[row.kind],
    calendar: row.calendar === 'hebrew' ? 'HEBREW' : 'GREGORIAN',
    day: row.day,
    ...(row.month ? { month: row.month } : {}),
    ...(row.hebrewMonth ? { hebrewMonth: row.hebrewMonth } : {}),
    ...(row.year ? { year: row.year } : {}),
    ...(row.label ? { label: row.label } : {}),
  };
}

function profileWire(row: CardProfileRow): StoredSocialProfile {
  return { network: row.network.toUpperCase(), ...(row.handle ? { handle: row.handle } : {}), ...(row.opens ? { url: row.opens } : {}) };
}

export function PersonCardSections({ personId, model }: { personId: string; model: PersonCardModel }) {
  const { t, i18n } = useTranslation();
  const productName = useProductName();
  const { logPageClickBi } = useGenericBi();
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const locale = i18n.language || 'en';
  const close = () => setDialog(null);
  const click = (button_name: string) => logPageClickBi({ page_name: PAGE, button_name });

  const suggested = <Badge label={t('people.card.source.inferred', { productName })} />;
  const forget = (edit: CardEdit, button: string) => () => {
    click(button);
    setDialog({ kind: 'forget', edit });
  };

  const addButton = (onClick: () => void, label: string) => (
    <Button variant="text" size="small" startIcon={<Plus size={16} aria-hidden />} onClick={onClick}>
      {label}
    </Button>
  );

  return (
    <>
      {/* How you two keep in touch (P3.1) — observed, read-only, and only what Symphony sees. */}
      {model.summary ? (
        <div className={styles.detailSection} data-testid="person-card-summary">
          <Typography variant="h3" color="primary">
            {t('people.card.summary')}
          </Typography>
          <Card variant="sunken" size="sm" className={styles.channelCard}>
            <span className={styles.channelText}>
              <Typography variant="p2" color="primary">
                {cardSummaryKeys(model.summary)
                  .map((key) => t(key))
                  .join(' · ')}
              </Typography>
              <Typography variant="p3" color="secondary">
                {t('people.card.summary.note', { productName })}
              </Typography>
            </span>
          </Card>
        </div>
      ) : null}

      {/* Relationship */}
      <div className={styles.detailSection}>
        <div className={styles.sectionHead}>
          <Typography variant="h3" color="primary">
            {t('people.card.relationship')}
          </Typography>
          {model.canAdd.relationships
            ? addButton(
                () => {
                  click('add_relationship');
                  setDialog({ kind: 'relationship', editing: null });
                },
                model.relationships.length ? t('people.card.relationship.add') : t('common.add'),
              )
            : null}
        </div>
        {model.relationships.length ? (
          <div className={styles.channelCards}>
            {model.relationships.map((row) => (
              <Card key={row.text} variant="sunken" size="sm" className={styles.channelCard}>
                <div className={styles.channelRow}>
                  <span className={styles.channelText}>
                    <Typography variant="h4" color="primary" className={styles.channelValue}>
                      {row.text}
                    </Typography>
                    {row.inferred ? <span className={styles.personCardBadge}>{suggested}</span> : null}
                  </span>
                  <RowMenu
                    ariaLabel={row.text}
                    onEdit={() => {
                      click('edit_relationship');
                      setDialog({ kind: 'relationship', editing: row });
                    }}
                    onForget={forget({ op: 'forget', field: 'relationship', target: relationshipWire(row) }, 'forget_relationship')}
                  />
                </div>
              </Card>
            ))}
          </div>
        ) : null}
      </div>

      {/* Important dates */}
      <div className={styles.detailSection}>
        <div className={styles.sectionHead}>
          <Typography variant="h3" color="primary">
            {t('people.card.dates')}
          </Typography>
          {model.canAdd.importantDates
            ? addButton(() => {
                click('add_important_date');
                setDialog({ kind: 'date', editing: null });
              }, t('common.add'))
            : null}
        </div>
        {model.importantDates.length ? (
          <div className={styles.channelCards}>
            {model.importantDates.map((row) => {
              const title = row.kind === 'custom' && row.label ? row.label : t(dateKindLabelKey(row.kind));
              const when = [
                row.age !== null ? t('people.card.dates.turns', { age: row.age }) : '',
                row.daysUntil !== null ? t(daysUntilKey(row.daysUntil, locale) as string, { count: row.daysUntil }) : '',
              ]
                .filter(Boolean)
                .join(' · ');
              return (
                <Card
                  key={`${row.kind}|${row.calendar}|${row.month ?? row.hebrewMonth}|${row.day}|${row.label ?? ''}`}
                  variant="sunken"
                  size="sm"
                  className={styles.channelCard}
                >
                  <div className={styles.channelRow}>
                    <span className={styles.channelText}>
                      <Typography variant="h4" color="primary">
                        {title}
                      </Typography>
                      <Typography variant="p2" color="secondary">
                        {formatCardDate(row, locale)}
                        {row.calendar === 'hebrew' ? ` · ${t('people.card.dates.hebrew')}` : ''}
                      </Typography>
                      {when ? (
                        <Typography variant="p3" color="secondary">
                          {when}
                        </Typography>
                      ) : null}
                      {row.inferred ? <span className={styles.personCardBadge}>{suggested}</span> : null}
                    </span>
                    <RowMenu
                      ariaLabel={title}
                      onEdit={() => {
                        click('edit_important_date');
                        setDialog({ kind: 'date', editing: row });
                      }}
                      onForget={forget({ op: 'forget', field: 'importantDate', target: dateWire(row) }, 'forget_important_date')}
                    />
                  </div>
                </Card>
              );
            })}
          </div>
        ) : null}
      </div>

      {/* Social profiles */}
      <div className={styles.detailSection}>
        <div className={styles.sectionHead}>
          <Typography variant="h3" color="primary">
            {t('people.card.social')}
          </Typography>
          {model.canAdd.socialProfiles
            ? addButton(() => {
                click('add_social_profile');
                setDialog({ kind: 'social' });
              }, t('common.add'))
            : null}
        </div>
        {model.socialProfiles.length ? (
          <div className={styles.channelCards}>
            {model.socialProfiles.map((row) => {
              const name = networkName(row.network, t);
              return (
                <Card key={`${row.network}|${row.handle ?? row.opens}`} variant="sunken" size="sm" className={styles.channelCard}>
                  <div className={styles.channelRow}>
                    <span className={styles.channelText}>
                      <Typography variant="h4" color="primary">
                        {name}
                      </Typography>
                      <Typography variant="p2" color="secondary" className={styles.channelValue}>
                        {/* A handle reads left-to-right in every language; `bdi` keeps it whole inside RTL text. */}
                        <bdi>{row.handle ? `@${row.handle}` : row.opens}</bdi>
                      </Typography>
                      {row.inferred ? <span className={styles.personCardBadge}>{suggested}</span> : null}
                    </span>
                    <span className={styles.personCardRowEnd}>
                      {row.opens ? (
                        <Button
                          variant="text"
                          size="small"
                          href={row.opens}
                          external
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`${name} ${row.handle ?? ''}`.trim()}
                          startIcon={<ExternalLink size={16} aria-hidden />}
                          onClick={() => click('open_social_profile')}
                        >
                          {t('common.open')}
                        </Button>
                      ) : null}
                      <RowMenu
                        ariaLabel={name}
                        onForget={forget({ op: 'forget', field: 'socialProfile', target: profileWire(row) }, 'forget_social_profile')}
                      />
                    </span>
                  </div>
                </Card>
              );
            })}
          </div>
        ) : null}
      </div>

      {/* Work */}
      <div className={styles.detailSection}>
        <div className={styles.sectionHead}>
          <Typography variant="h3" color="primary">
            {t('people.card.work')}
          </Typography>
          {!model.organization
            ? addButton(() => {
                click('add_work');
                setDialog({ kind: 'work', editing: null });
              }, t('common.add'))
            : null}
        </div>
        {model.organization ? (
          <Card variant="sunken" size="sm" className={styles.channelCard}>
            <div className={styles.channelRow}>
              <span className={styles.channelText}>
                {model.organization.company ? (
                  <Typography variant="h4" color="primary">
                    {model.organization.company}
                  </Typography>
                ) : null}
                {model.organization.title ? (
                  <Typography variant="p2" color="secondary">
                    {model.organization.title}
                  </Typography>
                ) : null}
                {model.organization.website ? (
                  <Typography variant="p3" color="secondary" className={styles.channelValue}>
                    <bdi>{model.organization.website}</bdi>
                  </Typography>
                ) : null}
                {model.organization.inferred ? <span className={styles.personCardBadge}>{suggested}</span> : null}
              </span>
              <RowMenu
                ariaLabel={t('people.card.work')}
                onEdit={() => {
                  click('edit_work');
                  setDialog({ kind: 'work', editing: model.organization });
                }}
                onForget={forget({ op: 'forget', field: 'organization' }, 'forget_work')}
              />
            </div>
          </Card>
        ) : null}
      </div>

      {dialog?.kind === 'relationship' ? <RelationshipDialog personId={personId} editing={dialog.editing} onClose={close} /> : null}
      {dialog?.kind === 'date' ? <DateDialog personId={personId} editing={dialog.editing} onClose={close} /> : null}
      {dialog?.kind === 'social' ? <SocialDialog personId={personId} onClose={close} /> : null}
      {dialog?.kind === 'work' ? <WorkDialog personId={personId} editing={dialog.editing} onClose={close} /> : null}
      {dialog?.kind === 'forget' ? <ForgetDialog personId={personId} edit={dialog.edit} onClose={close} /> : null}
    </>
  );
}

function networkName(network: CardNetwork, t: (key: string) => string): string {
  if (network === 'website') return t('people.card.social.website');
  if (network === 'other') return t('people.card.social.other');
  return NETWORK_NAMES[network];
}

/** A row's Edit / Forget actions, in the row overflow menu every list in the app uses. */
function RowMenu({ ariaLabel, onEdit, onForget }: { ariaLabel: string; onEdit?: () => void; onForget: () => void }) {
  const { t } = useTranslation();
  const actions: OverflowMenuAction[] = [
    ...(onEdit ? [{ key: 'edit', label: t('common.edit'), onSelect: onEdit }] : []),
    { key: 'forget', label: t('people.card.forget'), onSelect: onForget, destructive: true },
  ];
  return <OverflowMenu ariaLabel={ariaLabel} actions={actions} />;
}

/** Saves one edit, keeping the dialog open with the reader's input on a refusal. */
function useCardSave(personId: string, onClose: () => void) {
  const { logPageClickBi } = useGenericBi();
  const [busy, setBusy] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const save = async (edit: CardEdit, button: string) => {
    if (busy) return;
    setBusy(true);
    setErrorKey(null);
    logPageClickBi({ page_name: PAGE, button_name: button });
    const result = await editPersonCard(personId, edit);
    setBusy(false);
    if (!result.ok) {
      setErrorKey(result.messageKey);
      return;
    }
    onClose();
  };
  return { busy, errorKey, save, setErrorKey };
}

function DialogShell({ title, onClose, children, footer }: { title: string; onClose: () => void; children: ReactNode; footer: ReactNode }) {
  const narrow = useNarrow();
  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      size="minimum"
      maxHeightPx={650}
      scrollBody
      presentation={narrow ? 'sheet' : 'centered'}
      footer={footer}
    >
      <div className={styles.addChannelForm}>{children}</div>
    </Modal>
  );
}

function ErrorLine({ messageKey }: { messageKey: string | null }) {
  const { t } = useTranslation();
  if (!messageKey) return null;
  return (
    <Typography variant="p2" color="inherit" role="alert" className={styles.personCardError}>
      {t(messageKey)}
    </Typography>
  );
}

function RelationshipDialog({ personId, editing, onClose }: { personId: string; editing: CardRelationshipRow | null; onClose: () => void }) {
  const { t } = useTranslation();
  const [value, setValue] = useState(editing?.text ?? '');
  const { busy, errorKey, save } = useCardSave(personId, onClose);
  const submit = () => {
    const relation = value.trim();
    if (!relation) return;
    void save(
      editing
        ? {
            op: 'set',
            field: 'relationship',
            target: relationshipWire(editing),
            // An anchor ("Yoni's teacher") the relation already had is kept; only the words change.
            value: { relation, ...(editing.relatedToPersonId ? { relatedToPersonId: editing.relatedToPersonId } : {}) },
          }
        : { op: 'set', field: 'relationship', value: { relation } },
      editing ? 'save_relationship_edit' : 'save_relationship',
    );
  };
  return (
    <DialogShell
      title={t('people.card.relationship')}
      onClose={onClose}
      footer={
        <Button fullWidth loading={busy} disabled={!value.trim()} onClick={submit}>
          {t('common.save')}
        </Button>
      }
    >
      <Input
        label={t('people.card.relationship')}
        value={value}
        onChange={(event) => setValue(event.target.value)}
        placeholder={t('people.card.relationship.placeholder')}
        maxLength={200}
        status={errorKey ? 'error' : 'default'}
        statusMessage={errorKey ? t(errorKey) : undefined}
        autoFocus
      />
    </DialogShell>
  );
}

function DateDialog({ personId, editing, onClose }: { personId: string; editing: CardDateRow | null; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const [kind, setKind] = useState<CardDateKind>(editing?.kind ?? 'birthday');
  const [draft, setDraft] = useState<DateDraft>({
    hebrew: editing?.calendar === 'hebrew',
    day: editing ? String(editing.day) : '',
    month: editing?.month ?? 1,
    hebrewMonth: editing?.hebrewMonth ?? 'TISHREI',
    year: editing?.year ? String(editing.year) : '',
  });
  const { hebrew, month, hebrewMonth, day, year } = draft;
  const patch = (next: Partial<DateDraft>) => setDraft((d) => ({ ...d, ...next }));
  const [label, setLabel] = useState(editing?.label ?? '');
  const { busy, errorKey, save, setErrorKey } = useCardSave(personId, onClose);
  const gregorianNames = useMemo(() => gregorianMonthNames(locale), [locale]);
  const hebrewNames = useMemo(
    () => Object.fromEntries(HEBREW_MONTHS.map((m) => [m, hebrewMonthName(m, locale)])) as Record<HebrewMonth, string>,
    [locale],
  );
  // A Hebrew date's year is a Hebrew year: the field hints the current one ("5787"), never a civil "1983".
  const today = useMemo(() => civilToday(new Date()), []);
  const yearHint = hebrew ? String(currentHebrewYear(today)) : undefined;

  const kindActions: OverflowMenuAction[] = DATE_KINDS.map((k) => ({ key: k, label: t(dateKindLabelKey(k)), onSelect: () => setKind(k) }));
  // With a year typed, only the months that year has (Adar I/II in a leap year, Adar in a common one).
  const monthActions: OverflowMenuAction[] = hebrew
    ? hebrewMonthsForYear(year).map((m) => ({ key: m, label: hebrewNames[m], onSelect: () => patch({ hebrewMonth: m }) }))
    : gregorianNames.map((name, i) => ({ key: String(i + 1), label: name, onSelect: () => patch({ month: i + 1 }) }));

  const submit = () => {
    const result = dateDraftValue({ ...draft, kind, label }, today);
    if (!result.ok) {
      setErrorKey(result.errorKey);
      return;
    }
    const value: StoredImportantDate = result.value;
    void save(
      editing ? { op: 'set', field: 'importantDate', target: dateWire(editing), value } : { op: 'set', field: 'importantDate', value },
      editing ? 'save_important_date_edit' : 'save_important_date',
    );
  };

  return (
    <DialogShell
      title={t('people.card.dates')}
      onClose={onClose}
      footer={
        <Button fullWidth loading={busy} disabled={!day.trim()} onClick={submit}>
          {t('common.save')}
        </Button>
      }
    >
      <div className={styles.personCardField}>
        <Typography variant="h4" color="primary">
          {t('people.card.dates.kind')}
        </Typography>
        <OverflowMenu label={t(dateKindLabelKey(kind))} ariaLabel={t('people.card.dates.kind')} actions={kindActions} selectedKey={kind} />
      </div>
      {kind === 'custom' ? (
        <Input label={t('people.card.dates.label')} value={label} onChange={(e) => setLabel(e.target.value)} maxLength={100} />
      ) : null}
      <Switch checked={hebrew} onChange={(on: boolean) => setDraft((d) => switchDateDraftCalendar(d, on))} label={t('people.card.dates.hebrew')} />
      <div className={styles.personCardDateRow}>
        <Input
          label={t('people.card.dates.day')}
          value={day}
          onChange={(e) => patch({ day: e.target.value.replace(/\D/g, '').slice(0, 2) })}
          inputMode="numeric"
          className={styles.personCardDay}
        />
        <div className={styles.personCardField}>
          <Typography variant="h4" color="primary">
            {t('people.card.dates.month')}
          </Typography>
          <OverflowMenu
            label={hebrew ? hebrewNames[hebrewMonth] : gregorianNames[month - 1]}
            ariaLabel={t('people.card.dates.month')}
            actions={monthActions}
            selectedKey={hebrew ? hebrewMonth : String(month)}
          />
        </div>
      </div>
      <Input
        label={t('people.card.dates.year')}
        optionalLabel={t('common.optional')}
        value={year}
        onChange={(e) => patch({ year: e.target.value.replace(/\D/g, '').slice(0, 4) })}
        inputMode="numeric"
        placeholder={yearHint}
      />
      <ErrorLine messageKey={errorKey} />
    </DialogShell>
  );
}

function SocialDialog({ personId, onClose }: { personId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const [network, setNetwork] = useState<CardNetwork>('instagram');
  const [value, setValue] = useState('');
  const { busy, errorKey, save } = useCardSave(personId, onClose);
  const actions: OverflowMenuAction[] = NETWORK_ORDER.map((n) => ({ key: n, label: networkName(n, t), onSelect: () => setNetwork(n) }));
  const submit = () => {
    const typed = value.trim();
    if (!typed) return;
    // A homepage is only ever a link; elsewhere a typed link is the url and anything else the handle.
    const isUrl = network === 'website' || /^https?:\/\//i.test(typed);
    void save(
      { op: 'set', field: 'socialProfile', value: { network: network.toUpperCase(), ...(isUrl ? { url: typed } : { handle: typed }) } },
      'save_social_profile',
    );
  };
  return (
    <DialogShell
      title={t('people.card.social')}
      onClose={onClose}
      footer={
        <Button fullWidth loading={busy} disabled={!value.trim()} onClick={submit}>
          {t('common.save')}
        </Button>
      }
    >
      <div className={styles.personCardField}>
        <Typography variant="h4" color="primary">
          {t('people.card.social.network')}
        </Typography>
        <OverflowMenu label={networkName(network, t)} ariaLabel={t('people.card.social.network')} actions={actions} selectedKey={network} />
      </div>
      <Input
        label={t('people.card.social.handle')}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        maxLength={300}
        dir="ltr"
        status={errorKey ? 'error' : 'default'}
        statusMessage={errorKey ? t(errorKey) : undefined}
        autoFocus
      />
    </DialogShell>
  );
}

function WorkDialog({ personId, editing, onClose }: { personId: string; editing: CardOrganization | null; onClose: () => void }) {
  const { t } = useTranslation();
  const [company, setCompany] = useState(editing?.company ?? '');
  const [title, setTitle] = useState(editing?.title ?? '');
  const [website, setWebsite] = useState(editing?.website ?? '');
  const { busy, errorKey, save } = useCardSave(personId, onClose);
  const empty = !company.trim() && !title.trim() && !website.trim();
  const submit = () => {
    if (empty) return;
    void save(
      {
        op: 'set',
        field: 'organization',
        value: { company: company.trim(), title: title.trim(), website: website.trim() },
      },
      editing ? 'save_work_edit' : 'save_work',
    );
  };
  return (
    <DialogShell
      title={t('people.card.work')}
      onClose={onClose}
      footer={
        <Button fullWidth loading={busy} disabled={empty} onClick={submit}>
          {t('common.save')}
        </Button>
      }
    >
      <Input label={t('people.card.work.company')} value={company} onChange={(e) => setCompany(e.target.value)} maxLength={150} autoFocus />
      <Input label={t('people.card.work.title')} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={150} />
      <Input
        label={t('people.card.work.website')}
        optionalLabel={t('common.optional')}
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
        maxLength={300}
        dir="ltr"
        placeholder="https://"
      />
      <ErrorLine messageKey={errorKey} />
    </DialogShell>
  );
}

function ForgetDialog({ personId, edit, onClose }: { personId: string; edit: CardEdit; onClose: () => void }) {
  const { t } = useTranslation();
  const productName = useProductName();
  const { busy, errorKey, save } = useCardSave(personId, onClose);
  return (
    <DialogShell
      title={t('people.card.forget')}
      onClose={onClose}
      footer={
        <div className={styles.personCardFooter}>
          <Button variant="outlined" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button color="destructive" loading={busy} onClick={() => void save(edit, `confirm_forget_${edit.field}`)}>
            {t('people.card.forget')}
          </Button>
        </div>
      }
    >
      <Typography variant="p1" color="primary">
        {t('people.card.forget.confirm', { productName })}
      </Typography>
      <ErrorLine messageKey={errorKey} />
    </DialogShell>
  );
}
